import * as THREE from 'three';
import type { GhostTrackerScreenPosition } from './HUD';

export interface GhostMarkerViewport {
  width: number;
  height: number;
}

export interface GhostMarkerOptions {
  marginX?: number;
  marginTop?: number;
  marginBottom?: number;
  worldYOffset?: number;
}

const DEFAULT_MARGIN_X = 96;
const DEFAULT_MARGIN_TOP = 78;
const DEFAULT_MARGIN_BOTTOM = 122;
const DEFAULT_WORLD_Y_OFFSET = 2.2;

const projectVec = new THREE.Vector3();
const forwardVec = new THREE.Vector3();
const rightVec = new THREE.Vector3();
const toGhostVec = new THREE.Vector3();

export function projectGhostMarkerToScreen(
  worldPosition: THREE.Vector3,
  camera: THREE.Camera,
  viewport: GhostMarkerViewport,
  options: GhostMarkerOptions = {},
): GhostTrackerScreenPosition {
  const width = Math.max(1, viewport.width);
  const height = Math.max(1, viewport.height);
  const marginX = options.marginX ?? DEFAULT_MARGIN_X;
  const marginTop = options.marginTop ?? DEFAULT_MARGIN_TOP;
  const marginBottom = options.marginBottom ?? DEFAULT_MARGIN_BOTTOM;
  const minX = Math.min(marginX, width * 0.5);
  const maxX = Math.max(minX, width - marginX);
  const minY = Math.min(marginTop, height * 0.5);
  const maxY = Math.max(minY, height - marginBottom);

  projectVec.copy(worldPosition);
  projectVec.y += options.worldYOffset ?? DEFAULT_WORLD_Y_OFFSET;

  camera.updateMatrixWorld();
  camera.getWorldDirection(forwardVec);
  rightVec.setFromMatrixColumn(camera.matrixWorld, 0);
  toGhostVec.copy(projectVec).sub(camera.position);
  if (toGhostVec.dot(forwardVec) <= 0) {
    const fallbackX = toGhostVec.dot(rightVec) < 0 ? minX : maxX;
    return {
      x: Math.round(fallbackX),
      y: Math.round(maxY),
      pinned: true,
    };
  }

  projectVec.project(camera);
  if (!Number.isFinite(projectVec.x) || !Number.isFinite(projectVec.y) || !Number.isFinite(projectVec.z)) {
    return {
      x: Math.round(width * 0.5),
      y: Math.round(maxY),
      pinned: true,
    };
  }

  const rawX = (projectVec.x * 0.5 + 0.5) * width;
  const rawY = (-projectVec.y * 0.5 + 0.5) * height;
  const x = clamp(rawX, minX, maxX);
  const y = clamp(rawY, minY, maxY);
  return {
    x: Math.round(x),
    y: Math.round(y),
    pinned: x !== rawX || y !== rawY || projectVec.z < -1 || projectVec.z > 1,
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
